import type { SumsubMode, SumsubSettingsUpdateRequest } from "@repo/shared/types";
import { useState, useTransition } from "react";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { patch } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { FeatureCard, type PriceLine } from "./feature-card";
import { T } from "./sumsub-settings.i18n";

const PRICES: PriceLine[] = [
  { label: "Identity Document Verification", unit: "Per check", price: "0.495 EUR" },
  { label: "Liveness & Face match", unit: "Per check", price: "1.125 EUR" },
];

type SumsubSettingsProps = {
  sumsubMode: string;
  mutate: () => void;
};

export function SumsubSettings({ sumsubMode: initialMode, mutate }: SumsubSettingsProps) {
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mode, setMode] = useState<SumsubMode>(initialMode as SumsubMode);

  const modeHint =
    mode === "disabled"
      ? t.usingDisabled
      : mode === "production"
        ? t.usingProdCreds
        : t.usingTestCreds;

  // Auto-save on change — no save button. Revert the toggle if the write is rejected
  // (e.g. the disable-guard refuses while a flow still uses the component).
  const handleModeChange = (value: string) => {
    if (!value || value === mode) return;
    const previous = mode;
    setMode(value as SumsubMode);
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        await patch("/internal/sumsub/settings", {
          sumsubMode: value as SumsubMode,
        } satisfies SumsubSettingsUpdateRequest);
        setSuccess(t.settingsSaved);
        mutate();
      } catch (err: any) {
        setMode(previous);
        setError(err.message || t.failedToSave);
      }
    });
  };

  return (
    <FeatureCard
      title={t.title}
      description={t.description}
      prices={PRICES}
      vendor={{
        name: "Sum and Substance Ltd (Sumsub)",
        homepage: "https://sumsub.com",
        details: [
          "Reg. No. 09688671 (England & Wales)",
          "30 St. Mary Axe, London EC3A 8BF, United Kingdom",
        ],
        links: [
          { kind: "terms", href: "https://sumsub.com/terms-and-conditions/" },
          { kind: "status", href: "https://status.sumsub.com/" },
          { kind: "security", href: "https://trustreport.sumsub.com/" },
        ],
      }}
      footnote={t.pricesFootnote}
      error={error}
      success={success}
      control={
        <div className="flex flex-col items-end gap-1">
          <Label htmlFor="sumsub-mode" className="sr-only">
            {t.mode}
          </Label>
          <ToggleGroup
            id="sumsub-mode"
            type="single"
            value={mode}
            onValueChange={handleModeChange}
            disabled={isPending}
            className="w-fit"
          >
            <ToggleGroupItem value="disabled">{t.disabled}</ToggleGroupItem>
            <ToggleGroupItem value="test">{t.test}</ToggleGroupItem>
            <ToggleGroupItem value="production">{t.production}</ToggleGroupItem>
          </ToggleGroup>
          <p className="text-xs text-muted-foreground text-right max-w-[16rem]">{modeHint}</p>
        </div>
      }
    />
  );
}
